/**
 * Splash — full-viewport brand splash shown ONLY while auth state resolves.
 *
 * Rendered by App.tsx while `AuthContext.isLoading` is genuinely true (the async
 * `validateSession()` window). There is NO artificial minimum-display delay — it
 * disappears the instant auth resolves. Its job is to cover that window so a
 * logged-in user hard-refreshing a portal never flashes the login screen.
 *
 * BRAND-1 / SPL-1: rebuilt from `aisistoo-splash-live.html` — the v37 pinwheel
 * mark (four V-pieces fly in, crown + point settle, corner glows pulse) over the
 * brand-purple gradient, then the wordmark + attribution rise. Pure inline
 * CSS/SVG (no video, no Lottie, no network font); all classes are scoped under
 * `.aisistoo-splash` to avoid global collisions. Respects reduced-motion by
 * rendering the fully-settled mark statically (no animation).
 */
export function Splash() {
  return (
    <div
      className="aisistoo-splash"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'linear-gradient(180deg, #4B39C4 0%, #6C5CE7 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0,
        overflow: 'hidden',
      }}
    >
      <style>{`
        .aisistoo-splash svg.mark { width: 34vw; max-width: 200px; overflow: visible; }
        .aisistoo-splash .v-piece,
        .aisistoo-splash .crown,
        .aisistoo-splash .point {
          stroke: #fff; stroke-width: 7; fill: none; stroke-linecap: round; stroke-linejoin: round;
          transform-box: fill-box; transform-origin: center; opacity: 0;
        }
        @keyframes splashFlyIn {
          0%   { opacity: 0; transform: rotate(var(--start-rot)) translateY(calc(-1 * var(--fly-dist))) scale(0.7); }
          70%  { opacity: 1; transform: rotate(4deg) translateY(2px) scale(1.02); }
          100% { opacity: 1; transform: rotate(0deg) translateY(0) scale(1); }
        }
        .aisistoo-splash .v1 { --start-rot: -55deg; --fly-dist: 110px; animation: splashFlyIn .65s cubic-bezier(.2,.9,.25,1) .05s forwards; }
        .aisistoo-splash .v2 { --start-rot: -55deg; --fly-dist: 110px; animation: splashFlyIn .65s cubic-bezier(.2,.9,.25,1) .12s forwards; }
        .aisistoo-splash .v3 { --start-rot: -55deg; --fly-dist: 110px; animation: splashFlyIn .65s cubic-bezier(.2,.9,.25,1) .19s forwards; }
        .aisistoo-splash .v4 { --start-rot: -55deg; --fly-dist: 110px; animation: splashFlyIn .65s cubic-bezier(.2,.9,.25,1) .26s forwards; }
        .aisistoo-splash .crown { animation: splashSlideDown .5s ease-out .55s forwards; }
        .aisistoo-splash .point { animation: splashSlideUp .5s ease-out .58s forwards; }
        @keyframes splashSlideDown { from { opacity: 0; transform: translateY(-14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes splashSlideUp   { from { opacity: 0; transform: translateY(14px); }  to { opacity: 1; transform: translateY(0); } }
        .aisistoo-splash .glow { opacity: 0; }
        .aisistoo-splash .glow circle { fill: #fff; filter: blur(6px); }
        .aisistoo-splash .g1 { animation: splashPulse .9s ease-out .78s forwards; }
        .aisistoo-splash .g2 { animation: splashPulse .9s ease-out .90s forwards; }
        .aisistoo-splash .g3 { animation: splashPulse .9s ease-out 1.02s forwards; }
        .aisistoo-splash .g4 { animation: splashPulse .9s ease-out 1.14s forwards; }
        @keyframes splashPulse { 0% { opacity: 0; } 40% { opacity: .7; } 100% { opacity: 0; } }
        .aisistoo-splash .wordmark {
          font-family: var(--font-heading); font-weight: 800;
          font-size: clamp(28px, 9.5vw, 44px); letter-spacing: -0.02em;
          color: #fff; margin-top: 6px; opacity: 0; transform: translateY(14px);
          animation: splashRise .55s ease-out 1.05s forwards;
        }
        .aisistoo-splash .powered {
          font-family: var(--font-body); font-weight: 600;
          font-size: clamp(11px, 2.6vw, 13px); color: rgba(255,255,255,0.85);
          margin-top: 10px; opacity: 0; transform: translateY(10px);
          animation: splashRise .5s ease-out 1.30s forwards;
        }
        .aisistoo-splash .india {
          position: fixed; bottom: 6vh; left: 0; right: 0; text-align: center;
          font-family: var(--font-body); font-size: clamp(10px, 2.3vw, 12px);
          color: rgba(255,255,255,0.78); opacity: 0; transform: translateY(8px);
          animation: splashRise .5s ease-out 1.55s forwards;
        }
        @keyframes splashRise { to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) {
          .aisistoo-splash .v-piece,
          .aisistoo-splash .crown,
          .aisistoo-splash .point,
          .aisistoo-splash .wordmark,
          .aisistoo-splash .powered,
          .aisistoo-splash .india {
            animation: none !important; opacity: 1 !important; transform: none !important;
          }
          .aisistoo-splash .glow { display: none; }
        }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <svg className="mark" viewBox="0 0 100 100" aria-hidden="true">
          <path className="v-piece v1" d="M38.01,19.85 L51.66,42.17 L73.22,27.33" />
          <path className="v-piece v2" d="M80.15,38.01 L57.83,51.66 L72.67,73.22" />
          <path className="v-piece v3" d="M61.99,80.15 L48.34,57.83 L26.78,72.67" />
          <path className="v-piece v4" d="M19.85,61.99 L42.17,48.34 L27.33,26.78" />
          <line className="crown" x1="40" y1="12" x2="60" y2="12" />
          <path className="point" d="M45,90 L50,80 L55,90" />
          <g className="glow g1"><circle cx="79" cy="21" r="6" /></g>
          <g className="glow g2"><circle cx="79" cy="79" r="6" /></g>
          <g className="glow g3"><circle cx="21" cy="79" r="6" /></g>
          <g className="glow g4"><circle cx="21" cy="21" r="6" /></g>
        </svg>
        <div className="wordmark">Aisistoo</div>
        <div className="powered">Powered by Unosonic</div>
      </div>
      <div className="india">Made with love in India</div>
    </div>
  );
}

export default Splash;
